/**
 * Z Raporu (Daily Cashier) — gün sonu kapanış raporu.
 */
import {
  Alert, Button, Card, Divider, Grid, Group, Loader, Paper, SimpleGrid,
  Stack, Text, Title, Badge,
} from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import {
  IconCash, IconCreditCard, IconQrcode, IconReceipt, IconLock, IconCheck,
  IconAlertCircle, IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useDailyCashier, useCashierClosingState, useRequestZReport,
  useCompleteZReport, useCancelZReportRequest,
} from "../../../shared/api";
import { MoneyDisplay, PageHeader, KPICard, confirmAction } from "../../../shared/ui";

// Comp sebep kodu → TR etiket (backend pos.CompReason ile aynı).
const COMP_REASON_LABEL: Record<string, string> = {
  CUSTOMER_DISSATISFACTION: "Müşteri Beğenmedi",
  KITCHEN_ERROR: "Mutfak Hatası",
  LONG_WAIT: "Uzun Bekleme",
  GOODWILL: "Jest / İyi Niyet",
  OTHER: "Diğer",
};

export function DailyCashierPage() {
  const { data, isLoading } = useDailyCashier();
  const { data: closing } = useCashierClosingState();
  const requestZ = useRequestZReport();
  const completeZ = useCompleteZReport();
  const cancelZ = useCancelZReportRequest();

  if (isLoading) return <Loader />;
  if (!data) return <Text c="dimmed">Veri yok</Text>;

  const isClosing = closing?.closing_state === true;
  const openCount = closing?.open_order_count ?? 0;
  // Z raporu her zaman tamamlanabilir — açık masalar bloke etmez, ertesi güne
  // devredilir (backend complete_z_report). openCount sadece kaç masanın
  // devredileceğini sahibe gösterir.
  const hasOpen = openCount > 0;

  const handleRequest = async () => {
    try {
      const result = await requestZ.mutateAsync();
      if (result.open_order_count > 0) {
        notifications.show({
          title: "Gün sonu başladı",
          message: `${result.open_order_count} açık masa var — Z'yi tamamlarsanız bu masalar ertesi güne devredilir (açık kalır).`,
          color: "orange",
        });
      } else {
        notifications.show({
          title: "Açık masa yok",
          message: "Z raporu hemen üretilebilir — 'Z Raporu Tamamla' butonuna basabilirsiniz.",
          color: "blue",
        });
      }
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Hata",
        color: "red",
      });
    }
  };

  const handleComplete = async () => {
    // Açık masa varsa devir onayı al — sahip yanlışlıkla devretmesin.
    if (hasOpen) {
      if (!(await confirmAction({
        title: "Açık masalar devredilecek",
        message: `${openCount} masa hâlâ açık (ödenmemiş). Z raporunu tamamlarsanız bu masalar KAPATILMAZ; ertesi güne devredilir ve hesapları yarın ödenir. Devam edilsin mi?`,
        confirmLabel: "Tamamla ve Devret",
      }))) return;
    }
    try {
      const result = await completeZ.mutateAsync();
      const carried = result?.carried_over_count ?? 0;
      notifications.show({
        title: "Z Raporu Tamamlandı",
        message: carried > 0
          ? `Gün sonu kapatıldı. ${carried} masa ertesi güne devredildi. Sipariş kabulü yeniden açıldı.`
          : "Gün sonu kapatıldı. Sipariş kabulü yeniden açıldı.",
        color: "green",
        icon: <IconCheck />,
      });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Hata",
        color: "red",
      });
    }
  };

  const handleCancel = async () => {
    if (!(await confirmAction({
      title: "Kapanışı iptal et",
      message: "Kapanışı iptal etmek istediğinize emin misiniz? Sipariş kabulü tekrar açılacak.",
      confirmLabel: "Kapanışı İptal Et",
      danger: true,
    }))) return;
    try {
      await cancelZ.mutateAsync();
      notifications.show({
        message: "Kapanış iptal edildi — sipariş kabulü açıldı",
        color: "blue",
      });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Hata",
        color: "red",
      });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="📋 Z Raporu (Gün Sonu)"
        subtitle="Bugünün finansal özeti"
        actions={
          !isClosing ? (
            <Button
              leftSection={<IconLock size={16} />}
              color="orange"
              onClick={handleRequest}
              loading={requestZ.isPending}
            >
              Gün Sonu Başlat
            </Button>
          ) : (
            <Group gap="xs">
              <Button
                leftSection={<IconCheck size={16} />}
                color="green"
                onClick={handleComplete}
                loading={completeZ.isPending}
              >
                Z Raporu Tamamla
              </Button>
              <Button
                variant="light"
                color="gray"
                leftSection={<IconX size={16} />}
                onClick={handleCancel}
              >
                İptal Et
              </Button>
            </Group>
          )
        }
      />

      {/* Kapanış durumu banner */}
      {isClosing && (
        <Alert
          color={hasOpen ? "orange" : "green"}
          icon={hasOpen ? <IconAlertCircle /> : <IconCheck />}
          title={hasOpen ? "Kapanış Sürecinde — Açık Masalar Devredilecek" : "Z Raporu Hazır"}
          variant="filled"
        >
          {hasOpen ? (
            <Text>
              <strong>{openCount} açık masa</strong> hâlâ ödenmemiş. <strong>Z
              Raporu Tamamla</strong>'ya basarsanız bu masalar kapatılmaz; ertesi
              güne devredilir (açık kalır, hesapları yarın ödenir). Dilerseniz önce
              hesaplarını kapatabilirsiniz. Yeni sipariş kabulü şu an kilitli — Z
              tamamlanınca otomatik açılır.
            </Text>
          ) : (
            <Text>
              Tüm masalar kapandı. <strong>Z Raporu Tamamla</strong> butonuna
              basarak gün sonunu kapatın. Yeni sipariş kabulü kilitli — Z
              tamamlanınca otomatik açılır.
            </Text>
          )}
        </Alert>
      )}

      {/* GELİR Kısmı */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={3} mb="md" c="green">💰 Gelir</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <SummaryRow label="Toplam Sipariş" value={data.total_orders} />
          <SummaryRow label="İptal" value={data.cancelled_count} />
          <SummaryRow label="Bahşiş Toplam" value={<MoneyDisplay minor={data.tip_total_minor} />} />
          <SummaryRow label="İndirim Toplam" value={<MoneyDisplay minor={data.discount_total_minor} />} />
        </SimpleGrid>
        <Divider my="md" />
        <Group justify="space-between">
          <Text fw={700} size="xl">Toplam Ciro</Text>
          <Text fw={700} size="xl" c="green">
            <MoneyDisplay minor={data.total_revenue_minor} />
          </Text>
        </Group>
      </Paper>

      {/* ÖDEME TİPİ BREAKDOWN — büyük KPI */}
      <Title order={3} c="blue">💳 Ödeme Tipi Dağılımı</Title>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <KPICard
          title="💵 Nakit"
          value={<MoneyDisplay minor={data.cash_total_minor} />}
          icon={<IconCash size={24} />}
          color="green"
        />
        <KPICard
          title="💳 Kart"
          value={<MoneyDisplay minor={data.card_total_minor} />}
          icon={<IconCreditCard size={24} />}
          color="blue"
        />
        <KPICard
          title="📱 QR / Dijital"
          value={<MoneyDisplay minor={data.qr_payment_total_minor} />}
          icon={<IconQrcode size={24} />}
          color="violet"
        />
        <KPICard
          title="🧾 Toplam Tahsilat"
          value={<MoneyDisplay minor={(data.cash_total_minor || 0) + (data.card_total_minor || 0) + (data.qr_payment_total_minor || 0)} />}
          icon={<IconReceipt size={24} />}
          color="teal"
        />
      </SimpleGrid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">Ödeme Tipi Grafiği</Title>
            <DonutChart
              h={250}
              data={[
                { name: "Nakit", value: data.cash_total_minor, color: "green.6" },
                { name: "Kart", value: data.card_total_minor, color: "blue.6" },
                { name: "QR", value: data.qr_payment_total_minor, color: "violet.6" },
              ]}
              withLabels
              withTooltip
            />
            <Divider my="sm" />
            <Stack gap={4}>
              <Group justify="space-between">
                <Badge color="green" leftSection={<IconCash size={12} />}>Nakit</Badge>
                <Text fw={600}><MoneyDisplay minor={data.cash_total_minor} /></Text>
              </Group>
              <Group justify="space-between">
                <Badge color="blue" leftSection={<IconCreditCard size={12} />}>Kart</Badge>
                <Text fw={600}><MoneyDisplay minor={data.card_total_minor} /></Text>
              </Group>
              <Group justify="space-between">
                <Badge color="violet" leftSection={<IconQrcode size={12} />}>QR</Badge>
                <Text fw={600}><MoneyDisplay minor={data.qr_payment_total_minor} /></Text>
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">Sipariş Kaynak</Title>
            <Stack gap="sm">
              <SummaryRow label="POS" value={data.pos_order_count} />
              <SummaryRow label="QR Menü" value={data.qr_order_count} />
              <SummaryRow label="Delivery" value={data.delivery_order_count} />
              <Divider />
              <SummaryRow label="Ortalama Sipariş" value={<MoneyDisplay minor={data.avg_order_value_minor} />} />
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* GİDER Kısmı */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={3} mb="md" c="red">💸 Gider</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          <SummaryRow label="Genel Giderler" value={<MoneyDisplay minor={data.expenses_total_minor} />} />
          <SummaryRow label="Tedarikçi Ödemesi" value={<MoneyDisplay minor={data.procurement_paid_minor} />} />
          <SummaryRow label="Maaş" value={<MoneyDisplay minor={data.payroll_paid_minor} />} />
          <SummaryRow label="Avans" value={<MoneyDisplay minor={data.advance_paid_minor} />} />
          <SummaryRow
            label={`Personel Yemeği (${data.staff_meal_count ?? 0} adet)`}
            value={<MoneyDisplay minor={data.staff_meal_cost_minor ?? 0} />}
          />
        </SimpleGrid>
        <Divider my="md" />
        <Group justify="space-between">
          <Text fw={700} size="xl">Toplam Gider</Text>
          <Text fw={700} size="xl" c="red">
            <MoneyDisplay minor={data.total_expenses_minor} />
          </Text>
        </Group>
      </Paper>

      {/* ZAYIAT (Sprint 32) */}
      <Paper p="lg" radius="md" withBorder style={{ borderColor: (data.total_loss_minor ?? 0) > 0 ? "#fa5252" : undefined }}>
        <Title order={3} mb="md" c="orange">🗑️ Zayiat (Fire + Düzeltme)</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <SummaryRow
            label={`Fire Kayıtları (${data.waste_count ?? 0} adet)`}
            value={<MoneyDisplay minor={data.waste_total_minor ?? 0} />}
          />
          <SummaryRow
            label={`Sayım Düzeltme Kaybı (${data.adjustment_count ?? 0} adet)`}
            value={<MoneyDisplay minor={data.adjustment_loss_minor ?? 0} />}
          />
        </SimpleGrid>
        <Divider my="md" />
        <Group justify="space-between">
          <Text fw={700} size="xl">Toplam Zayiat</Text>
          <Text fw={700} size="xl" c={(data.total_loss_minor ?? 0) > 0 ? "red" : "dimmed"}>
            <MoneyDisplay minor={data.total_loss_minor ?? 0} />
          </Text>
        </Group>
        {(data.total_loss_minor ?? 0) > 0 && (
          <Text size="xs" c="dimmed" mt="xs">
            Fire (bozulma/döküldü/expired) + ADJUSTED türü negatif sayım
            düzeltmeleri (örn. "sistem 10 kg gösteriyordu, gerçekte 1 kg").
            Net kârdan düşülmüş.
          </Text>
        )}
      </Paper>

      {/* COMP (İkram) — müşteriye gitti, ücret alınmadı. Bedel zaten ciroyu
          düşürdü; bu kart MALİYETİ (food cost) görünür kılar, net kâra EK
          düşüm yok (çift sayım önleme). */}
      <Paper p="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3} c="pink">🎁 İkram (Comp)</Title>
          <Text size="sm" c="dimmed">
            {data.comp_count ?? 0} adet · maliyet
          </Text>
        </Group>
        <Group justify="space-between">
          <Text fw={700} size="xl">Toplam İkram Maliyeti</Text>
          <Text fw={700} size="xl" c={(data.comp_total_minor ?? 0) > 0 ? "pink" : "dimmed"}>
            <MoneyDisplay minor={data.comp_total_minor ?? 0} />
          </Text>
        </Group>
        {Object.keys(data.comp_by_reason ?? {}).length > 0 && (
          <>
            <Divider my="md" />
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {Object.entries(data.comp_by_reason as Record<string, { total_minor: number; count: number }>).map(
                ([reason, b]) => (
                  <SummaryRow
                    key={reason}
                    label={`${COMP_REASON_LABEL[reason] ?? reason} (${b.count} adet)`}
                    value={<MoneyDisplay minor={b.total_minor} />}
                  />
                ),
              )}
            </SimpleGrid>
          </>
        )}
        <Text size="xs" c="dimmed" mt="xs">
          İkram = müşteriye gitti, ücret alınmadı (void değil). Bedel zaten
          ciroyu düşürdü — net kâra ayrıca eklenmez; bu maliyet food cost
          görünürlüğü içindir.
        </Text>
      </Paper>

      {/* NET KÂR */}
      <Card padding="xl" radius="md" withBorder style={{ background: "linear-gradient(135deg,#f0fff4,#e6fcf5)" }}>
        <Group justify="space-between">
          <Title order={2}>💎 Net Kâr</Title>
          <Title order={1} c={data.net_profit_minor >= 0 ? "teal" : "red"}>
            <MoneyDisplay minor={data.net_profit_minor} />
          </Title>
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          Net = Ciro − Giderler − Zayiat
        </Text>
      </Card>
    </Stack>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={700} size="lg">{value}</Text>
    </Stack>
  );
}
